<template>
  <div ref="wrapperRef" :class="rootClass" :style="$attrs.style as any">
    <label v-if="label" :for="inputId" class="tw:mb-1.5 tw:block tw:text-13 tw:font-medium tw:text-gray-700">
      {{ label }}
      <span v-if="required" aria-hidden="true" class="tw:text-red-600">&nbsp;*</span>
    </label>
    <!-- relative anchor for the panel so `top-full` measures from the trigger,
         not the wrapper (which also holds the hint/error message below). Attrs like data-testid land
         here so a testid-scoped query can reach the open panel's options. -->
    <div class="tw:relative tw:w-full" v-bind="forwardedAttrs">
      <!-- trigger: single = fixed h-control; multi = min-h-control so chips can wrap -->
      <div
        :class="
          cn(
            TRIGGER_BASE,
            multiple ? MULTI_METRICS : SINGLE_METRICS,
            showSingleClear ? 'tw:pr-[55px]' : 'tw:pr-[35px]',
            isInvalid && GROUP_INVALID,
            disabled && GROUP_DISABLED
          )
        "
        @pointerdown="onTriggerPointerDown">
        <!-- single-mode: avatar sits to the left of the query input, only when a selection has one -->
        <span
          v-if="!multiple && singleSelected && hasAvatar(singleSelected)"
          :class="OPTION_AVATAR_IN_FIELD"
          aria-hidden="true">
          <img
            v-if="singleSelected.avatarSrc"
            :src="singleSelected.avatarSrc"
            :alt="singleSelected.label"
            class="tw:size-full tw:object-cover" />
          <span v-else>{{ singleSelected.avatarInitials }}</span>
        </span>

        <!-- multi-mode: one chip per selection -->
        <template v-if="multiple">
          <span v-for="option in multiSelected" :key="option.value" :class="CHIP">
            <span v-if="hasAvatar(option)" :class="[CHIP_AVATAR, PILL_AVATAR]" aria-hidden="true">
              <img
                v-if="option.avatarSrc"
                :src="option.avatarSrc"
                :alt="option.label"
                class="tw:size-full tw:object-cover" />
              <span v-else>{{ option.avatarInitials }}</span>
            </span>
            {{ option.label }}
            <button
              v-if="clearable && !disabled"
              type="button"
              :aria-label="`${option.label} entfernen`"
              :class="CHIP_REMOVE"
              @pointerdown.stop
              @click.stop="removeValue(option.value)">
              <IconX :size="12" :stroke-width="2" />
            </button>
          </span>
        </template>

        <input
          :id="inputId"
          ref="inputRef"
          v-model="query"
          type="text"
          role="combobox"
          autocomplete="off"
          :readonly="!filterable"
          :aria-expanded="isOpen"
          :aria-controls="listboxId"
          aria-autocomplete="list"
          :aria-invalid="isInvalid || undefined"
          :placeholder="triggerPlaceholder"
          :disabled="disabled"
          :class="[
            GROUP_CONTROL,
            multiple && multiSelected.length > 0 && query === '' ? 'tw:min-w-0' : 'tw:min-w-8',
            multiple && 'tw:h-[22px]',
            disabled && 'tw:text-inherit',
          ]"
          @focus="onFocus"
          @input="onInput"
          @keydown="onKeydown" />

        <!-- single-mode: clear ✕ next to the caret -->
        <button
          v-if="!multiple && clearable && hasSingleSelection && !disabled"
          type="button"
          aria-label="Clear"
          :class="CLEAR"
          @pointerdown.stop
          @click.stop="clearAll">
          <IconX :size="16" />
        </button>

        <IconChevronDown :size="16" :class="CARET" aria-hidden="true" />
      </div>

      <!-- panel -->
      <div v-if="isOpen" :id="listboxId" role="listbox" :aria-multiselectable="multiple || undefined" :class="PANEL">
        <p v-if="filtered.length === 0" :class="EMPTY">{{ emptyText }}</p>
        <div
          v-for="(option, index) in filtered"
          v-else
          :id="`${uid}-opt-${option.value}`"
          :key="option.value"
          role="option"
          :aria-selected="isSelected(option)"
          :aria-disabled="isRowDisabled(option) || undefined"
          :class="[
            ROW,
            !isRowDisabled(option) && 'tw:hover:bg-gray-50',
            index === activeIndex && !isRowDisabled(option) && ROW_ACTIVE,
            isRowDisabled(option) && ROW_DISABLED,
          ]"
          @pointerdown.prevent
          @click.stop="!isRowDisabled(option) && toggle(option)"
          @pointerenter="!isRowDisabled(option) && (activeIndex = index)">
          <span v-if="hasAvatar(option)" :class="[OPTION_AVATAR_IN_ROW]" aria-hidden="true">
            <img
              v-if="option.avatarSrc"
              :src="option.avatarSrc"
              :alt="option.label"
              class="tw:size-full tw:object-cover" />
            <span v-else>{{ option.avatarInitials }}</span>
          </span>
          <span class="tw:min-w-0 tw:flex-1 tw:truncate">{{ option.label }}</span>
          <span v-if="option.count != null" class="tw:shrink-0 tw:text-13 tw:font-normal tw:leading-5 tw:text-muted">
            {{ option.count }}
          </span>
          <IconCheck :size="16" :class="['tw:shrink-0 tw:text-primary', !isSelected(option) && 'tw:invisible']" />
        </div>
      </div>
    </div>

    <!-- error takes precedence over hint (matches LeadInput / DS Field) -->
    <span v-if="firstError" class="tw:mt-1.5 tw:block tw:text-13 tw:font-normal tw:text-red-600">
      {{ firstError }}
    </span>
    <span v-else-if="$slots.hint" class="tw:mt-1.5 tw:block tw:text-13 tw:font-normal tw:text-gray-600">
      <slot name="hint" />
    </span>
  </div>
</template>

<script lang="ts">
export interface LeadSelectOption {
  /** What the select reports and what a `name` posts. */
  value: string;
  /** What the row reads, and what the query filters on. */
  label: string;
  /** Listable but inert. Still shown — a row that vanishes reads as a bug. */
  disabled?: boolean;
  /** Number pushed to the row's far edge. */
  count?: number;
  /** Photo. Falls back to `avatarInitials` when absent. */
  avatarSrc?: string;
  /** Usually two letters. Renders when no `avatarSrc`. */
  avatarInitials?: string;
}
</script>

<script setup lang="ts">
// LeadSelect — single or multi picker, painted with LEAD DS control styles.
// Single mode mirrors DS <Select>: 36px h-control, selected label shown in
// the input. Multi mode mirrors DS <MultiSelect>: min-h-control grows with
// wrapped selection pills, query input flex-1 among them.
import { computed, nextTick, onMounted, onUnmounted, ref, useAttrs, useId, watch } from 'vue';
import { IconCheck, IconChevronDown, IconX } from '@tabler/icons-vue';
import { cn } from '@/lib/cn';

// Opt out of automatic `$attrs.class` merging so consumer classes flow through
// cn() and can safely override our root wrapper (e.g. `class="tw:w-64"` beats
// our `tw:w-full`).
defineOptions({ inheritAttrs: false });

const props = withDefaults(
  defineProps<{
    options: LeadSelectOption[];
    label?: string;
    required?: boolean;
    placeholder?: string;
    emptyText?: string;
    /** Multi-select. Model becomes `string[]`. */
    multiple?: boolean;
    /** Single: show an ✕ in the trigger. Multi: show ✕ on each chip. */
    clearable?: boolean;
    /** Type-to-filter. Off makes the input readonly. */
    filterable?: boolean;
    disabled?: boolean;
    /** Paints the error ring without a message. Pair with `errors` for both. */
    invalid?: boolean;
    /** Validation error(s). First non-empty entry shows below the field and forces the invalid ring. */
    errors?: string[] | string | null;
    id?: string;
  }>(),
  {
    label: undefined,
    required: false,
    placeholder: 'Auswählen…',
    emptyText: 'Keine Treffer',
    multiple: false,
    clearable: false,
    filterable: true,
    disabled: false,
    invalid: false,
    errors: null,
    id: undefined,
  }
);

// v-model: string | null in single mode, string[] in multi mode.
const modelValue = defineModel<string | string[] | null>({ required: true });

/* --------------------------------- STATE ---------------------------------- */

const uid = useId();
const inputId = computed(() => props.id ?? `${uid}-input`);
const listboxId = `${uid}-list`;

const isOpen = ref(false);
const query = ref('');
const activeIndex = ref(0);
const wrapperRef = ref<HTMLDivElement | null>(null);
const inputRef = ref<HTMLInputElement | null>(null);

/* --------------------------------- COMPUTED ------------------------------- */

const singleSelected = computed(() => {
  if (props.multiple) return null;
  const v = modelValue.value;
  if (v == null || v === '') return null;
  return props.options.find((o) => o.value === v) ?? null;
});

const hasSingleSelection = computed(() => singleSelected.value != null);

// Consumer classes on the root, merged through cn() so `class="tw:w-64"` beats
// our default `tw:w-full` without needing `!`.
const attrs = useAttrs();
const rootClass = computed(() => cn('tw:w-full', attrs.class as string | undefined));

// everything else (data-testid, aria-*, ...) forwards onto the trigger+panel anchor
const forwardedAttrs = computed(() => {
  const { class: _c, style: _s, id: _i, ...rest } = attrs as any;
  return rest;
});

const firstError = computed(() => {
  const e = props.errors;
  if (e == null) return null;
  const list = Array.isArray(e) ? e : [e];
  return list.find((m) => !!m) ?? null;
});
// `errors` implies invalid, so callers don't have to pass both.
const isInvalid = computed(() => props.invalid || firstError.value != null);

const showSingleClear = computed(
  () => !props.multiple && props.clearable && hasSingleSelection.value && !props.disabled
);

const multiValues = computed<string[]>(() => {
  if (!props.multiple) return [];
  const v = modelValue.value;
  return Array.isArray(v) ? v : [];
});

// Reported in `options` declaration order — click order stays out of the result.
const multiSelected = computed(() =>
  props.multiple ? props.options.filter((o) => multiValues.value.includes(o.value)) : []
);

const filtered = computed(() => {
  const needle = query.value.trim().toLowerCase();
  if (needle === '') return props.options;
  return props.options.filter((o) => o.label.toLowerCase().includes(needle));
});

// Single mode: when closed, the input renders the selected label; when open with
// a selection, we clear the value and show the label AS the placeholder so the
// user can type-to-filter over all options while still seeing what's picked.
// Multi mode: chips carry the selection, placeholder only shows when nothing picked.
const triggerPlaceholder = computed(() => {
  if (props.multiple) return multiSelected.value.length === 0 ? props.placeholder : '';
  if (isOpen.value && hasSingleSelection.value) return singleSelected.value!.label;
  if (hasSingleSelection.value) return '';
  return props.placeholder;
});

/* ---------------- DS class strings (verbatim, tw:-prefixed) --------------- */

// TRIGGER_BASE = shared with GROUP_STATES from controlStyles.ts — no fixed height here.
// Metrics differ between single (h-control, one row) and multi (min-h-control, wraps).
const TRIGGER_BASE = [
  'tw:relative tw:flex tw:w-full tw:items-center tw:gap-2',
  'tw:rounded-md tw:border tw:border-solid tw:border-gray-300 tw:bg-white tw:shadow-xs',
  'tw:transition-[border-color,box-shadow] tw:duration-150 tw:ease-in-out',
  'tw:has-enabled:hover:border-primary',
  'tw:focus-within:border-primary tw:focus-within:shadow-[0_1px_2px_0_rgb(16_24_40/0.05),0_0_0_4px_rgb(0_122_255/0.1)]',
  // right padding is applied conditionally in the template — 35px reserves the caret column
  // (DS Select / MultiSelect); 55px adds room for the clear ✕ when visible.
].join(' ');

// single: fixed 36px like Input — 1 + 7 + 20 + 7 + 1
const SINGLE_METRICS = 'tw:h-control tw:px-[11px] tw:py-1.75 tw:cursor-text';

// multi: grows with wrapped chips; 1 + 6 + 22 + 6 + 1 = 36 at rest
const MULTI_METRICS = 'tw:flex-wrap tw:min-h-control tw:px-[11px] tw:py-1.5 tw:cursor-text';

const GROUP_INVALID =
  'tw:border-red-600! tw:has-enabled:hover:border-red-600 tw:focus-within:border-red-600 tw:focus-within:shadow-[0_1px_2px_0_rgb(16_24_40/0.05),0_0_0_4px_rgb(231_0_11/0.1)]';

const GROUP_DISABLED = 'tw:cursor-not-allowed tw:bg-light tw:text-[rgb(16_24_40/0.3)]';

// query input, chrome stripped — flex-1 so it shares its row with chips
const GROUP_CONTROL = [
  'tw:min-w-0 tw:flex-1 tw:appearance-none tw:border-0 tw:bg-transparent tw:p-0',
  'tw:w-auto tw:rounded-none tw:shadow-none tw:outline-none',
  'tw:text-14 tw:font-normal tw:text-gray-900',
  'tw:placeholder:text-[rgb(16_24_40/0.3)] tw:placeholder:opacity-100',
  'tw:focus:outline-none tw:disabled:cursor-not-allowed',
].join(' ');

// caret: absolutely positioned so it holds the first row however many rows the pills wrap to.
// First row centre: 1 + 6 + 11 = 18px, less half the 16px glyph → top-[10px].
const CARET = 'tw:pointer-events-none tw:absolute tw:right-[11px] tw:top-[10px] tw:text-gray-400';

const CLEAR = [
  'tw:inline-flex tw:shrink-0 tw:cursor-pointer tw:items-center tw:justify-center tw:rounded-sm',
  'tw:text-gray-400 tw:transition-colors tw:duration-150 tw:ease-in-out tw:hover:text-gray-600',
  'tw:focus-visible:outline-none tw:focus-visible:shadow-[0_0_0_4px_rgb(0_122_255/0.1)] tw:focus-visible:text-gray-600',
  'tw:border-0 tw:bg-transparent tw:p-0',
  // sit before the caret column
  'tw:absolute tw:right-[31px] tw:top-[10px]',
].join(' ');

// chip = DS Pill (neutral tone, overridden to text-gray-700 like MultiSelect does)
const CHIP = [
  'tw:inline-flex tw:items-center tw:justify-center tw:gap-1 tw:whitespace-nowrap',
  'tw:rounded-full tw:border tw:border-solid tw:border-gray-200 tw:bg-white',
  'tw:px-[9px] tw:py-px tw:text-12 tw:font-medium tw:text-gray-700',
].join(' ');

// ✕ inside a chip — 12px glyph in p-px = 14px, fits in the 18px content row
const CHIP_REMOVE = [
  'tw:-mr-[3px] tw:inline-flex tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:p-px',
  'tw:cursor-pointer tw:opacity-60 tw:transition-opacity tw:duration-150 tw:ease-in-out tw:hover:opacity-100',
  'tw:focus-visible:outline-none tw:focus-visible:shadow-[0_0_0_4px_rgb(0_122_255/0.1)] tw:focus-visible:opacity-100',
  'tw:border-0 tw:bg-transparent',
].join(' ');

// avatar inside a chip: 18px in 18px content row, pulled 7px left where text sits at 9
const CHIP_AVATAR = [
  'tw:inline-flex tw:shrink-0 tw:items-center tw:justify-center tw:overflow-hidden',
  'tw:size-[18px] tw:rounded-sm tw:bg-gray-100 tw:text-10 tw:font-medium tw:uppercase tw:text-muted',
].join(' ');
const PILL_AVATAR = 'tw:-ml-[7px]';

// avatar in single-mode field: 24px in a 20px row → bleed -2px into padding, pull -5px left
const OPTION_AVATAR_IN_FIELD = [
  'tw:inline-flex tw:shrink-0 tw:items-center tw:justify-center tw:overflow-hidden',
  'tw:size-6 tw:rounded-sm tw:bg-gray-100 tw:text-[11px] tw:font-medium tw:uppercase tw:text-muted',
  'tw:-my-[2px] tw:-ml-[5px]',
].join(' ');

// avatar in a listbox row: same 24px, same bleed/pull
const OPTION_AVATAR_IN_ROW = [
  'tw:inline-flex tw:shrink-0 tw:items-center tw:justify-center tw:overflow-hidden',
  'tw:size-6 tw:rounded-sm tw:bg-gray-100 tw:text-[11px] tw:font-medium tw:uppercase tw:text-muted',
  'tw:-my-[2px] tw:-ml-[5px]',
].join(' ');

const PANEL = [
  'tw:absolute tw:inset-x-0 tw:top-full tw:z-20 tw:mt-1! tw:max-h-64 tw:overflow-auto',
  'tw:rounded-md tw:border tw:border-solid tw:border-gray-300 tw:bg-white tw:py-1 tw:shadow-raised',
  'tw:pl-0!',
].join(' ');

const ROW = [
  'tw:flex tw:w-full tw:cursor-pointer tw:items-center tw:gap-2',
  'tw:px-[11px] tw:py-1.5 tw:text-left tw:text-14 tw:font-normal tw:leading-5 tw:text-gray-900',
].join(' ');
const ROW_ACTIVE = 'tw:bg-gray-50';
const ROW_DISABLED = 'tw:cursor-not-allowed tw:text-[rgb(16_24_40/0.3)]';
const EMPTY = 'tw:px-[11px] tw:py-1.5 tw:text-14 tw:font-normal tw:leading-5 tw:text-muted';

/* --------------------------------- METHODS -------------------------------- */

function hasAvatar(o: LeadSelectOption) {
  return o.avatarSrc != null || o.avatarInitials != null;
}

function isSelected(o: LeadSelectOption) {
  if (props.multiple) return multiValues.value.includes(o.value);
  return singleSelected.value?.value === o.value;
}

function isRowDisabled(o: LeadSelectOption) {
  return o.disabled === true;
}

function setOpen(next: boolean) {
  isOpen.value = next;
  // reopening onto a stale query hides most of the list for no reason the user can see
  if (!next) query.value = '';
}

function openPanel() {
  if (isOpen.value) return;
  activeIndex.value = 0;
  isOpen.value = true;
}

function commitSingle(value: string | null) {
  modelValue.value = value;
}

function commitMulti(values: string[]) {
  // report in options order, not click order
  modelValue.value = props.options.filter((o) => values.includes(o.value)).map((o) => o.value);
}

function toggle(option: LeadSelectOption) {
  if (props.disabled || isRowDisabled(option)) return;
  if (props.multiple) {
    const current = multiValues.value;
    const next = current.includes(option.value)
      ? current.filter((v) => v !== option.value)
      : [...current, option.value];
    commitMulti(next);
    query.value = '';
    nextTick(() => inputRef.value?.focus());
  } else {
    commitSingle(option.value);
    query.value = '';
    setOpen(false);
  }
}

function removeValue(value: string) {
  if (!props.multiple) return;
  commitMulti(multiValues.value.filter((v) => v !== value));
}

function clearAll() {
  if (props.multiple) commitMulti([]);
  else commitSingle(null);
  query.value = '';
  nextTick(() => inputRef.value?.focus());
}

function nextEnabledIndex(from: number, step: number): number {
  const rows = filtered.value;
  for (let i = from + step; i >= 0 && i < rows.length; i += step) {
    if (!isRowDisabled(rows[i])) return i;
  }
  return from;
}

/* --------------------------------- HANDLERS ------------------------------- */

function onTriggerPointerDown(event: PointerEvent) {
  if (props.disabled) return;
  // pointerdown on a chip's ✕ has already stopped propagating; anything else means
  // "use this control" — focus the input and open the panel, but let a click that
  // starts inside the input reach it (so caret placement works in filterable mode).
  if (event.target !== inputRef.value) event.preventDefault();
  inputRef.value?.focus();
  if (!isOpen.value) openPanel();
}

function onFocus(_event: FocusEvent) {
  // Opening the panel clears the query (see the watch below), so typing filters
  // over the full list and the selected label is shown as a placeholder instead.
  if (!isOpen.value) openPanel();
}

function onInput() {
  if (!props.filterable) {
    // readonly should already stop keystrokes, but guard against paste etc.
    query.value = '';
    return;
  }
  activeIndex.value = 0;
  if (!isOpen.value) openPanel();
}

function onKeydown(event: KeyboardEvent) {
  switch (event.key) {
    case 'ArrowDown':
    case 'ArrowUp': {
      event.preventDefault();
      if (!isOpen.value) {
        openPanel();
        return;
      }
      activeIndex.value = nextEnabledIndex(activeIndex.value, event.key === 'ArrowDown' ? 1 : -1);
      return;
    }
    case 'Enter': {
      // never submit the enclosing form by accident
      event.preventDefault();
      if (!isOpen.value) {
        openPanel();
        return;
      }
      const option = filtered.value[activeIndex.value];
      if (option != null) toggle(option);
      return;
    }
    case 'Tab': {
      // focus moves on by itself; only the panel needs closing
      if (isOpen.value) setOpen(false);
      return;
    }
    case 'Escape': {
      if (isOpen.value) {
        event.stopPropagation();
        setOpen(false);
      }
      return;
    }
    case 'Backspace': {
      // multi: only on empty query — feels like erasing backwards through the field
      if (props.multiple && query.value === '' && multiSelected.value.length > 0) {
        event.preventDefault();
        removeValue(multiSelected.value[multiSelected.value.length - 1].value);
        return;
      }
      // single + clearable: backspace on an empty (or full-selection) input clears
      if (!props.multiple && props.clearable && hasSingleSelection.value) {
        const target = event.target as HTMLInputElement;
        if (target.value === '' || target.value === singleSelected.value?.label) {
          event.preventDefault();
          clearAll();
        }
      }
      return;
    }
  }
}

// Single mode: input reads as the selected label while closed (DS <Select>'s
// display-value pattern), and empties on open so the user can filter over the
// full list. The selected label shows as the input's placeholder while open.
watch(
  [singleSelected, isOpen],
  () => {
    if (props.multiple) return;
    if (isOpen.value) query.value = '';
    else query.value = singleSelected.value?.label ?? '';
  },
  { immediate: true }
);

/* --------------------------------- DISMISS -------------------------------- */
// pointerdown (not click): a drag that starts inside the panel and ends outside
// it should not dismiss.

function onDocPointerDown(event: PointerEvent) {
  if (!isOpen.value) return;
  if (!wrapperRef.value?.contains(event.target as Node)) setOpen(false);
}

onMounted(() => document.addEventListener('pointerdown', onDocPointerDown));
onUnmounted(() => document.removeEventListener('pointerdown', onDocPointerDown));
</script>
